import { useEffect, useRef } from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/solid';

// StatusStepper — the shared state-machine renderer (P10 §2.2/§2.4, story
// 2-141). "Stepper enforces state machine, disabled steps tooltip the
// reason" — and, same contract as `StatusChip` beside it, "the primitive
// holds no module-specific transition rules — modules pass their states in".
//
// ── What "enforces" means here ─────────────────────────────────────────────
// This is a RENDERER, not a state machine of its own: it enforces nothing by
// computing legality — it enforces the caller's own machine being the only
// source of truth, by giving every step exactly four renderable states
// (`complete` / `current` / `upcoming` / `blocked`) and refusing to invent a
// fifth. A step is `blocked` — never merely `upcoming` — the instant the
// caller says it cannot be reached from here, and a `blocked` step's reason
// is REQUIRED, not optional: this file will not render a disabled step with
// nothing said about why, the same "never a tooltip alone" discipline
// `InspectionLifecycleBar` already established for its own blocked actions
// (a title-only tooltip is unreadable on touch and unannounced on focus).
// `reason` renders as a real text node under the step AND is exposed via
// `aria-describedby`, so hover is a convenience, not the only path to it.
//
// ── `StepMarker` is exported (story 2-147k) ────────────────────────────────
// `SectionNav` (`modules/capture/components/SectionNav.tsx`) reuses this
// EXACT component for its own vertical timeline rail — same circle, same
// shape-per-state, same tone tokens — rather than a look-alike copy, per
// direct user feedback asking for one shared "timeline" design language
// across the capture panel's rail AND its inspector panel. `SectionNav`'s
// own states are a narrower two-value set (`done`/not) mapped onto
// `complete`/`current`/`upcoming` — never `blocked`, which is a real-actions
// concept that has no section-nav equivalent.
export type StatusStepState = 'complete' | 'current' | 'upcoming' | 'blocked';

export interface StatusStepperStep {
  id: string;
  label: string;
  state: StatusStepState;
  /** Why this step cannot be reached from here. REQUIRED when `state` is `blocked` — enforced by the component refusing to render a blocked step silently. */
  reason?: string;
}

export interface StatusStepperProps {
  steps: StatusStepperStep[];
  /** The stepper's own accessible name (it is a `<nav>` landmark-shaped list, not a page heading). */
  'aria-label': string;
  /**
   * `'stacked'` (default): the original story-2-141 behaviour, unchanged —
   * a vertical list that becomes a full-width `sm:flex-row` sequence at
   * `sm:` and up, each step sharing the row equally (`flex-1`).
   * `'horizontal'`: always a single row, `overflow-x-auto` rather than
   * shrinking steps to fit — for a narrow, fixed-width mount where a long
   * sequence needs to scroll rather than compress into unreadable markers.
   * `'vertical'` (story 2-147k, direct user feedback: "we want that to be
   * vertical... we have space but need to scroll horizontally right now"):
   * always a single COLUMN, dot-and-continuous-line — the shape
   * `docs/design/sitewise-ux-reference.html`'s own `.swa-version-panel`/
   * `.swa-version-row` already establishes for a chronological/history list
   * (found live in that file, never carried into any React component before
   * this), reused here as the panel's own real consumer rather than a
   * markup-only reference. For a narrow, fixed-width sidebar TALL enough to
   * read top-to-bottom without scrolling — `InspectionLifecycleBar`'s
   * capture variant's real replacement for `horizontal`.
   */
  layout?: 'stacked' | 'horizontal' | 'vertical';
  className?: string;
  'data-testid'?: string;
}

const MARKER_TONE: Record<StatusStepState, string> = {
  complete: '--success',
  current: '--primary',
  upcoming: '--muted-foreground',
  blocked: '--destructive',
};

export function StepMarker({ state }: { state: StatusStepState }): JSX.Element {
  const v = MARKER_TONE[state];
  // Shape, not just colour, per step state (WCAG 1.4.1, same discipline as
  // StatusChip beside this file): complete is a check glyph, current is a
  // solid ring, blocked is a lock glyph, upcoming is a plain hollow ring —
  // four distinct silhouettes that survive a grayscale render.
  if (state === 'complete') {
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `hsl(var(${v}))` }}
        aria-hidden
      >
        <CheckIcon className="h-3.5 w-3.5 text-white" />
      </span>
    );
  }
  if (state === 'blocked') {
    return (
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2"
        style={{ borderColor: `hsl(var(${v}))`, color: `hsl(var(${v}))` }}
        aria-hidden
      >
        <LockClosedIcon className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2"
      style={{
        borderColor: `hsl(var(${v}))`,
        backgroundColor: state === 'current' ? `hsl(var(${v}) / 0.15)` : 'transparent',
      }}
      aria-hidden
    >
      {state === 'current' && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(var(${v}))` }} />}
    </span>
  );
}

export function StatusStepper({
  steps,
  'aria-label': ariaLabel,
  layout = 'stacked',
  className = '',
  'data-testid': testId,
}: StatusStepperProps): JSX.Element {
  const horizontal = layout === 'horizontal';
  const vertical = layout === 'vertical';
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const currentRef = useRef<HTMLLIElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Horizontal mode only: a fixed-width, scrollable mount (a sidebar panel,
  // not a full page width) can genuinely start with the CURRENT step
  // scrolled out of view — live-confirmed on `InspectionLifecycleBar`'s
  // capture variant (story 2-147k), where the one thing this component
  // exists to say ("here is where you are") was the one thing off-screen by
  // default. Deliberately sets `scrollLeft` on the LOCAL container directly
  // rather than calling `Element.scrollIntoView()`: that API walks EVERY
  // scrollable ancestor looking for one to move, not just this component's
  // own — live-confirmed (story-2-146c check 1, keyboard-focus-order)
  // cascading into the page's outer scroll/sticky-positioning context and
  // corrupting Tab order elsewhere on the page as a side effect. Direct
  // `scrollLeft` math can only ever touch this one element. `vertical` mode
  // has no analogous problem: a tall, fixed-width panel reading top-to-
  // bottom has room to show every step without scrolling (that's the whole
  // reason it replaced `horizontal` there), so it does not need this.
  useEffect(() => {
    const container = scrollRef.current;
    const target = currentRef.current;
    if (!horizontal || !container || !target) return;
    const targetCenter = target.offsetLeft + target.offsetWidth / 2;
    container.scrollLeft = targetCenter - container.clientWidth / 2;
  }, [horizontal, currentIndex]);

  const olClassName = vertical
    ? `flex flex-col gap-0 ${className}`
    : horizontal
      ? 'flex flex-row flex-nowrap items-start gap-0'
      : `flex flex-col gap-0 sm:flex-row sm:items-start sm:gap-0 ${className}`;

  const list = (
    <ol aria-label={ariaLabel} data-testid={horizontal ? undefined : testId} className={olClassName}>
      {steps.map((step, i) => {
        const reasonId = step.reason ? `${step.id}-reason` : undefined;
        const isLast = i === steps.length - 1;

        if (vertical) {
          return (
            <li
              key={step.id}
              className={`flex gap-3 ${!isLast ? 'pb-5' : ''}`}
              {...(step.state === 'current' ? { 'aria-current': 'step' as const } : {})}
            >
              <div className="flex flex-col items-center self-stretch">
                <StepMarker state={step.state} />
                {!isLast && (
                  <span
                    aria-hidden
                    className={`mt-1 w-px flex-1 ${step.state === 'complete' ? 'bg-success' : 'bg-border'}`}
                  />
                )}
              </div>
              <div className="min-w-0 pt-0.5">
                <p
                  className={`text-sm font-semibold ${step.state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground'}`}
                  {...(reasonId ? { 'aria-describedby': reasonId } : {})}
                >
                  {step.label}
                </p>
                {step.state === 'blocked' && step.reason && (
                  <p id={reasonId} className="mt-0.5 max-w-[16rem] text-xs text-muted-foreground">
                    {step.reason}
                  </p>
                )}
              </div>
            </li>
          );
        }

        return (
          <li
            key={step.id}
            ref={horizontal && step.state === 'current' ? currentRef : undefined}
            className={
              horizontal
                ? `flex shrink-0 flex-col items-start gap-0 ${!isLast ? 'pr-4' : ''}`
                : `flex flex-1 gap-3 sm:flex-col sm:items-start ${!isLast ? 'pb-4 sm:pb-0' : ''}`
            }
            {...(step.state === 'current' ? { 'aria-current': 'step' as const } : {})}
          >
            <div className={horizontal ? 'flex w-full min-w-[6rem] items-center' : 'flex items-center sm:w-full'}>
              <StepMarker state={step.state} />
              {!isLast && (
                <span
                  aria-hidden
                  className={`${horizontal ? 'ml-2 block' : 'hidden sm:ml-2 sm:block'} h-px flex-1 ${step.state === 'complete' ? 'bg-success' : 'bg-border'}`}
                />
              )}
            </div>
            <div className="min-w-0 mt-2">
              <p
                className={`text-sm font-semibold ${step.state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground'}`}
                {...(reasonId ? { 'aria-describedby': reasonId } : {})}
              >
                {step.label}
              </p>
              {step.state === 'blocked' && step.reason && (
                <p id={reasonId} className="mt-0.5 max-w-[16rem] text-xs text-muted-foreground">
                  {step.reason}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
  if (!horizontal) return list;
  return (
    // Story 2-148 part 7 finding, fixed here: a horizontally-scrollable
    // region with no focusable way to reach it is unusable by keyboard
    // (axe's `scrollable-region-focusable`, `serious`, WCAG 2.1.1) — a real
    // gap since this container's own children are `<li>` steps, not
    // necessarily all individually focusable. `tabIndex={0}` makes the
    // scroll region itself a stop that arrow-key/Page-Up/Down scrolling
    // works from, the standard fix for this exact axe rule.
    <div ref={scrollRef} tabIndex={0} data-testid={testId} className={`overflow-x-auto ${className}`}>
      {list}
    </div>
  );
}
